import cn from 'classnames';
import dayjs from 'dayjs';
import { FC, forwardRef, HTMLProps } from 'react';
import {
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight,
} from 'react-icons/md';
import { IconButton } from '../../../../components/icon-button/icon-button';
import s from './date-picker-calendar.module.css';
import { DatePeriod, useDatePickerCalendar } from './use-date-picker-calendar';
import { Button } from '../../../../components/button/button';
import { Card } from '../../../../components/card/card';

interface Props extends HTMLProps<HTMLDivElement> {
  selectedDate?: Date;
  handleSelectDate: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  handleReset?: () => void;
  handleClose?: () => void;
  type?: DatePeriod;
  disabledOutOfMonth?: boolean;
}

export const DatePickerCalendar: FC<Props> = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    handleSelectDate,
    maxDate,
    minDate,
    selectedDate,
    handleReset,
    className,
    handleClose,
    type = 'day',
    disabledOutOfMonth = true,
    ...other
  } = props;

  const {
    isCurrentDate,
    isSelectedDate,

    displayedDates,
    displayedMonth,
    displayedYear,
    daysInMonth,
    daysInPrevMonth,
    daysInWeek,
    emptyCellsAtEnd,
    firstDayOfMonth,
    handleNextMonth,
    handleNextYear,
    handlePrevMonth,
    handlePrevYear,

    t,
  } = useDatePickerCalendar(selectedDate);

  return (
    <Card className={cn(s.feature, className)} {...other} ref={ref}>
      <div className={s.header}>
        <IconButton
          onClick={handlePrevYear}
          size="sm"
          disabled={minDate && new Date(displayedYear - 1, displayedMonth, 1) < minDate}
        >
          <MdKeyboardDoubleArrowLeft />
        </IconButton>
        <IconButton
          onClick={handlePrevMonth}
          size="sm"
          disabled={minDate && new Date(displayedYear, displayedMonth, 1) < minDate}
        >
          <MdKeyboardArrowLeft />
        </IconButton>
        <span style={{ whiteSpace: 'nowrap' }}>{dayjs(displayedDates).format('MMMM YYYY')}</span>
        <IconButton
          onClick={handleNextMonth}
          size="sm"
          disabled={maxDate && new Date(displayedYear, displayedMonth + 1, 1) > maxDate}
        >
          <MdKeyboardArrowRight />
        </IconButton>
        <IconButton
          onClick={handleNextYear}
          size="sm"
          disabled={maxDate && new Date(displayedYear + 1, displayedMonth, 1) > maxDate}
        >
          <MdKeyboardDoubleArrowRight />
        </IconButton>
      </div>
      <div className={s.grid}>
        {daysInWeek.map((dayName) => (
          <div key={dayName} className={s.dayName}>
            {dayName}
          </div>
        ))}
        {Array.from({ length: firstDayOfMonth }).map((_, index) => {
          const cellDate = new Date(
            displayedYear,
            displayedMonth - 1,
            daysInPrevMonth - firstDayOfMonth + index + 1,
          );
          const isToday = isCurrentDate(cellDate, type);
          const isSelected = isSelectedDate(cellDate, type, selectedDate);
          return (
            <IconButton
              key={`prev-${index}`}
              className={cn(
                s.day,
                isSelected && s.selected,
                isToday && s.today,
                disabledOutOfMonth && s.extendDay,
              )}
              variant="ghost"
              onClick={() => !disabledOutOfMonth && handleSelectDate(cellDate)}
            >
              {daysInPrevMonth - firstDayOfMonth + index + 1}
            </IconButton>
          );
        })}
        {Array.from({ length: daysInMonth }).map((_, index) => {
          const day = index + 1;

          const isDisabledPrev =
            minDate && new Date(displayedYear, displayedMonth, day + 1) < minDate;
          const isDisabledNext = maxDate && new Date(displayedYear, displayedMonth, day) > maxDate;
          const isDisabled = isDisabledNext || isDisabledPrev;

          const cellDate = new Date(displayedYear, displayedMonth, day);

          const isSelected = isSelectedDate(cellDate, type, selectedDate);
          const isToday = isCurrentDate(cellDate, type);

          return (
            <IconButton
              key={day}
              className={cn(
                s.day,
                isSelected && s.selected,
                isToday && s.today,
                isDisabled && s.disabled,
              )}
              onClick={() => handleSelectDate(cellDate)}
              variant="ghost"
            >
              {day}
            </IconButton>
          );
        })}
        {Array.from({ length: emptyCellsAtEnd }).map((_, index) => {
          const cellDate = new Date(displayedYear, displayedMonth + 1, index + 1);
          const isToday = isCurrentDate(cellDate, type);
          const isSelected = isSelectedDate(cellDate, type, selectedDate);

          return (
            <IconButton
              key={`next-${index}`}
              className={cn(
                s.day,
                isSelected && s.selected,
                isToday && s.today,
                disabledOutOfMonth && s.extendDay,
              )}
              variant="ghost"
              onClick={() => !disabledOutOfMonth && handleSelectDate(cellDate)}
            >
              {index + 1}
            </IconButton>
          );
        })}
      </div>
      <div className={s.controls}>
        {handleClose && (
          <Button onClick={handleClose} variant="secondary">
            {t('Назад')}
          </Button>
        )}
        {handleReset && <Button onClick={handleReset}>{t('Сбросить')}</Button>}
      </div>
    </Card>
  );
});
